// 3D models for the Indoor Unit Corner on the VRV page: all 15 Daikin VRV
// indoor units. Built by scripts/vrv-models.mjs to the dimensions in Daikin's
// VRV Alpha catalogue; files in public/models/vrv/. No imports, so any script
// can load it.
//
// scene:   the unit as installed, with the room around it, and its animations
//          (Off, Opening, On, Closing).
// shown:   which size is drawn, where Daikin makes more than one.
// ar:      the files for "see it in your room", per colour where the colour
//          changes the unit (GLB for Android, USDZ for iPhone). None for the
//          ducted units: they are hidden above the ceiling.
// arMode:  'face-up' (a ceiling unit, lying face up on the floor: phone AR
//          places things on floors and walls, not ceilings), 'wall' (on a
//          wall on Android; iPhone puts it on the floor) or 'floor'.
// xray:    the material made see-through ('Ceiling', or 'BoxFront' for the
//          boxing round a concealed unit), with its button label.
// colours: what each swatch (named as in UNIT_LOOKS) does to the model's
//          named materials.
// camera:  where the view starts and how far it can turn.
// offNote / onNote: one line under the view. Every claim in them is from
//          Daikin's catalogue; where the layout is ours, they say so.

export const MODEL_DIR = '/models/vrv';

const PANEL_WHITE = { Panel: '#f4f3ee', Grille: '#e6e5df', Slat: '#d4d2ca', Slot: '#2f343b' };
const PANEL_BLACK = { Panel: '#1f2937', Grille: '#2b3138', Slat: '#3c434c', Slot: '#0d1117' };
const GRILLES = {
  White: { Grille: '#ffffff', Slat: '#e5e7eb', Slot: '#2f343b' },
  Black: { Grille: '#1f2937', Slat: '#374151', Slot: '#0d1117' },
};
const CEILING_CAMERA = (radius, target = '0m -0.1m 0m') => ({
  orbit: `35deg 116deg ${radius}m`, target, min: 'auto 95deg 1.2m', max: 'auto 172deg 6m',
});
const CEILING_XRAY = { xray: 'Ceiling', xrayLabel: 'See through the ceiling' };

export const MODELS_3D = {
  'round-flow-cassette': {
    scene: 'fxfsq-scene',
    shown: '950 × 950 mm panel',
    ar: { 'Fresh White': 'fxfsq-white-ar', Black: 'fxfsq-black-ar' },
    arMode: 'face-up',
    ...CEILING_XRAY,
    colours: { 'Fresh White': PANEL_WHITE, Black: PANEL_BLACK },
    camera: CEILING_CAMERA(2.5),
    exposure: 1.2,
    offNote: 'Switched off.',
    onNote: 'Switched on: air all round, 360°.',
  },
  'compact-cassette': {
    scene: 'fxzq-scene',
    shown: 'one 600 × 600 ceiling tile',
    ar: { White: 'fxzq-ar' },
    arMode: 'face-up',
    ...CEILING_XRAY,
    colours: { White: { Panel: '#f2f2f2', Grille: '#eceeef', Slat: '#cfd3d6' } },
    camera: CEILING_CAMERA(2.0),
    exposure: 1.2,
    offNote: 'Switched off: flush in its ceiling tile, only 8 mm below it.',
    onNote: 'Switched on: air in four directions.',
  },
  'one-way-cassette': {
    scene: 'fxkq-scene',
    shown: '1,390 × 595 mm panel',
    ar: { White: 'fxkq-white-ar', 'White on black': 'fxkq-white-black-ar', 'Silver on black': 'fxkq-silver-black-ar' },
    arMode: 'face-up',
    xray: null,
    colours: {
      White: { Panel: '#f6f6f4', Base: '#f6f6f4', Perf: '#d8dad9' },
      'White on black': { Panel: '#f6f6f4', Base: '#1d2126', Perf: '#3b4148' },
      'Silver on black': { Panel: '#c5c9cd', Base: '#1d2126', Perf: '#3b4148' },
    },
    camera: { orbit: '28deg 110deg 2.3m', target: '0m -0.2m 0m', min: '-70deg 95deg 1.2m', max: '70deg 165deg 5m' },
    exposure: 1.15,
    offNote: 'Switched off.',
    onNote: 'Switched on: the air goes one way, across the room.',
  },
  'two-way-cassette': {
    scene: 'fxcq-scene',
    shown: '1,285 × 700 mm panel (Daikin also makes 1,070 and 1,740 mm)',
    ar: { 'Fresh White': 'fxcq-ar' },
    arMode: 'face-up',
    ...CEILING_XRAY,
    colours: { 'Fresh White': PANEL_WHITE },
    camera: CEILING_CAMERA(2.7),
    exposure: 1.2,
    offNote: 'Switched off: the flat flaps close and no intake grilles show.',
    onNote: 'Switched on: air out of both long sides.',
  },
  'ceiling-suspended': {
    scene: 'fxhq-scene',
    shown: '195 × 1,160 × 680 mm (the 2.0 TR size)',
    ar: { Default: 'fxhq-ar' },
    arMode: 'wall',
    xray: null,
    colours: { Default: {} },
    camera: { orbit: '30deg 100deg 3m', target: '0m -0.3m 0.45m', min: '-75deg 70deg 1.2m', max: '75deg 165deg 6m' },
    exposure: 1.1,
    offNote: 'Switched off: the flap closes.',
    onNote: 'Switched on: the flap opens and the air goes out across the room.',
  },
  'four-way-suspended': {
    scene: 'fxuq-scene',
    shown: '198 × 950 × 950 mm',
    ar: { 'Fresh White': 'fxuq-ar' },
    arMode: 'face-up',
    xray: null,
    colours: { 'Fresh White': { Panel: '#f4f3ee' } },
    camera: { orbit: '35deg 108deg 2.9m', target: '0m -0.15m 0m', min: 'auto 92deg 1.2m', max: 'auto 172deg 6m' },
    exposure: 1.15,
    offNote: 'Switched off: the flaps close, for a simple look.',
    onNote: 'Switched on: air in up to four directions.',
  },
  'wall-mounted': {
    scene: 'fxaq-scene',
    shown: '298 × 929 × 258 mm (up to 2.0 TR)',
    ar: { White: 'fxaq-ar' },
    arMode: 'wall',
    xray: null,
    colours: { White: { Panel: '#f2f2f2' } },
    camera: { orbit: '30deg 82deg 2.4m', target: '0m 0.02m 0.4m', min: '-75deg 55deg 1m', max: '75deg 125deg 5m' },
    exposure: 1.05,
    offNote: 'Switched off: the louvre closes.',
    onNote: 'Switched on: the louvre opens and swings the air down into the room.',
  },
  'floor-standing': {
    scene: 'fxlq-scene',
    shown: '600 × 1,140 × 222 mm (the 1.0 TR size)',
    ar: { 'Ivory white': 'fxlq-ar' },
    arMode: 'floor',
    xray: null,
    colours: { 'Ivory white': { Panel: '#e4e0d2' } },
    camera: { orbit: '30deg 78deg 3.2m', target: '0m 0.75m 0.4m', min: '-75deg 35deg 1.2m', max: '75deg 100deg 6m' },
    exposure: 1.05,
    offNote: 'Switched off.',
    onNote: 'Switched on: the air leaves through the discharge grille on top.',
  },
  'concealed-floor': {
    scene: 'fxnq-scene',
    shown: '610 × 1,070 × 220 mm (the 1.0 TR size)',
    ar: { Default: 'fxnq-ar' },
    arMode: 'floor',
    arLabel: 'See the unit’s size on your floor',
    arNote: 'The bare unit at its true size: your boxing hides it.',
    xray: 'BoxFront',
    xrayLabel: 'See inside the boxing',
    colours: { Default: {} },
    camera: { orbit: '30deg 76deg 3.2m', target: '0m 0.6m 0.45m', min: '-75deg 35deg 1.2m', max: '75deg 100deg 6m' },
    exposure: 1.05,
    offNote: 'Only the grilles show. Look inside the boxing to see the unit.',
    onNote: 'Switched on: in this layout, air leaves the top grille and comes back in low at the front.',
  },
  'slim-duct': {
    scene: 'fxdq-scene',
    shown: '200 × 900 × 620 mm (1.3–1.6 TR)',
    ar: null,
    ...CEILING_XRAY,
    colours: GRILLES,
    camera: { orbit: '32deg 115deg 3.1m', target: '0m -0.05m -0.1m', min: 'auto 95deg 1.4m', max: 'auto 172deg 6m' },
    exposure: 1.2,
    offNote: 'Only the grilles show. See through the ceiling to find the unit.',
    onNote: 'Switched on: air from the slot grille, back in through the return grille.',
  },
  'mid-duct': {
    scene: 'fxmqa-scene',
    shown: '300 × 1,000 × 700 mm (the larger sizes)',
    ar: null,
    ...CEILING_XRAY,
    colours: GRILLES,
    camera: { orbit: '30deg 112deg 3.9m', target: '0m -0.1m -0.3m', min: 'auto 95deg 1.4m', max: 'auto 172deg 7m' },
    exposure: 1.2,
    offNote: 'Only the grilles show. See through the ceiling to find the unit.',
    onNote: 'Switched on: in this layout, a short duct feeds two diffusers.',
  },
  'high-duct': {
    scene: 'fxmqp-scene',
    shown: '300 × 1,000 × 700 mm',
    ar: null,
    ...CEILING_XRAY,
    colours: GRILLES,
    camera: { orbit: '28deg 108deg 5m', target: '0m -0.2m 0.1m', min: 'auto 95deg 1.6m', max: 'auto 172deg 8m' },
    exposure: 1.2,
    offNote: 'Only the grilles show. Here the unit sits over the passage, behind the wall.',
    onNote: 'Switched on: in this layout, one unit feeds four diffusers through ducts.',
  },
  'large-duct': {
    scene: 'fxmqn-scene',
    shown: '440 × 1,190 × 1,090 mm',
    ar: null,
    ...CEILING_XRAY,
    colours: GRILLES,
    camera: { orbit: '28deg 110deg 6.4m', target: '0m -0.3m -0.3m', min: 'auto 95deg 2m', max: 'auto 172deg 10m' },
    exposure: 1.2,
    offNote: 'Only the grilles show. See through the ceiling to find the unit.',
    onNote: 'Switched on: in this layout, one unit feeds six diffusers across a hall.',
  },
  'ahu-duct': {
    scene: 'fxvq-scene',
    shown: '1,670 × 950 × 510 mm (the 6.4 TR size)',
    ar: { Default: 'fxvq-ar' },
    arMode: 'floor',
    ...CEILING_XRAY,
    colours: GRILLES,
    camera: { orbit: '30deg 92deg 5.4m', target: '0.9m 1.75m 0.9m', min: '-60deg 35deg 2m', max: '80deg 120deg 10m' },
    exposure: 1.1,
    offNote: 'It stands in its own room (its walls are left out here); the hall sees only grilles.',
    onNote: 'Switched on: in this layout, the air goes up the duct to the hall’s diffusers.',
  },
  'spot-cooler': {
    scene: 'fxpq-scene',
    shown: '455 × 555 × 470 mm',
    ar: { Default: 'fxpq-ar' },
    arMode: 'floor',
    xray: null,
    colours: { Default: {} },
    camera: { orbit: '30deg 88deg 3m', target: '0m -0.6m 0.6m', min: '-75deg 50deg 1.2m', max: '75deg 140deg 6m' },
    exposure: 1.1,
    offNote: 'Switched off. Here it hangs from the slab; it can also rest on a stand.',
    onNote: 'Switched on: a jet of air at one spot.',
  },
};

const AR_TEXT = {
  'face-up': {
    label: 'See its true size on your floor',
    note: 'Phone AR puts things on floors and walls, not ceilings, so it lies face up on your floor, at its true size.',
  },
  wall: {
    label: 'See it on your wall',
    note: 'On Android it goes on your wall. On an iPhone it stands on your floor, at its true size.',
  },
  floor: {
    label: 'See it in your room',
    note: 'At its true size, on your floor.',
  },
};
export const arText = (cfg) => ({
  label: cfg.arLabel || (AR_TEXT[cfg.arMode] || AR_TEXT.floor).label,
  note: cfg.arNote || (AR_TEXT[cfg.arMode] || AR_TEXT.floor).note,
});

// The line on the card that opens the 3D view.
export const teaser = (cfg) => {
  if (!cfg.ar) return 'Turn it around, switch it on, and look through the ceiling to see the hidden unit.';
  if (cfg.xray === 'BoxFront') return 'Look inside the boxing, switch it on, and on a phone, see the unit’s size in your room.';
  return 'Turn it around, switch it on and watch the air, and on a phone, see it in your room.';
};

export const has3D = (id) => !!MODELS_3D[id];
export const modelFile = (name, ext = 'glb') => `${MODEL_DIR}/${name}.${ext}`;
